import type {
  CardContent,
  ChatCardItem,
  ChatCardItemState,
  ChatCardTone,
  ChatMessage,
  ExtractedFact,
  FactRef,
} from '@karehero/models';

import type { BenefitsCalculatorAnswers } from 'config/benefitsCalculatorV2Steps';
import { READING } from 'config/onboardingV4/copy';
import type { NeedItem } from 'config/onboardingV4/needs';
import type { TriageQuestion } from 'config/triageQuestions';
import { findFact } from 'utils/extractedFacts';
import { phrase, type Person } from 'utils/person';

// What onboarding v4 shows: the messages as they are drawn, with the
// checklists ticking as facts land.

export interface Reading {
  hasVoiceNote: boolean;
  isAllRead: boolean;
  questions: readonly TriageQuestion[];
  person: Person;
  /** The answers read from what they said, by question. */
  predicted: BenefitsCalculatorAnswers;
}

/** What is looked for while they are read: what they sent, then each question's answer. */
export const readingItems = ({
  hasVoiceNote,
  isAllRead,
  questions,
  person,
  predicted,
}: Reading): ChatCardItem[] => [
  {
    label: hasVoiceNote
      ? READING.firstItem.voiceNote
      : READING.firstItem.written,
    state: isAllRead ? 'done' : 'doing',
  },
  ...questions.map((q) => ({
    label: phrase(q.lookingFor, person),
    state: predicted[q.id] ? ('done' as const) : ('waiting' as const),
  })),
];

const hasValue = (fact: ExtractedFact) =>
  fact.factValue != null && fact.factValue !== '';

/** Whether a fact with a value has been read for this topic and name, whatever is read without one. */
const hasFact = (
  facts: readonly ExtractedFact[],
  { topicSlug, factName }: FactRef,
) => !!findFact(facts.filter(hasValue), topicSlug, factName);

export interface Needs {
  items: readonly NeedItem[];
  facts: readonly ExtractedFact[];
  person: Person;
  /** While they send what we need, the first left is marked as next. */
  isGathering: boolean;
}

/** What we would need, ticked as read or still being said. */
export const needItems = ({
  items,
  facts,
  person,
  isGathering,
}: Needs): ChatCardItem[] => {
  const known = items.map((item) =>
    item.isKnown ? item.isKnown(facts) : hasFact(facts, item.fact),
  );
  const next = known.indexOf(false);
  return items.map((item, i) => ({
    label: phrase(item.label, person),
    state: known[i] ? 'done' : isGathering && i === next ? 'doing' : 'waiting',
  }));
};

const isCard = (m: ChatMessage, tone: ChatCardTone) =>
  m.content.kind === 'card' && m.content.tone === tone;

/** Their words in a message: what they typed, or a voice note's transcript. */
const wordsOf = ({ from, content }: ChatMessage): string | undefined => {
  if (from !== 'user') return undefined;
  if (content.kind === 'text') return content.text;
  if (content.kind === 'voice') return content.transcript;
  return undefined;
};

/** The messages after the prompts card, up to the latest of what we heard. */
const sincePrompts = (messages: readonly ChatMessage[]): ChatMessage[] => {
  const start = messages.findIndex((m) => isCard(m, 'prompts'));
  if (start < 0) return [];
  const after = messages.slice(start + 1);
  const end = after.map((m) => isCard(m, 'heard')).lastIndexOf(true);
  return end < 0 ? after : after.slice(0, end);
};

/** What they said after the prompts card, up to the latest of what we heard: what ticks the prompts. */
export const saidSincePrompts = (messages: readonly ChatMessage[]): string =>
  sincePrompts(messages)
    .flatMap((m) => wordsOf(m) || [])
    .join('\n');

export interface Shown {
  /** Replies shown as the pick on their menu, left out of the thread. */
  hiddenIDs: readonly string[];
  /** Sent, and shown as being read until they are. */
  sentForReading: readonly string[];
  isRead: (id: string) => boolean;
  /** The opening prompts' ticks, by prompt. */
  promptStates: Readonly<Record<string, ChatCardItemState>>;
  isPromptsPinnable: boolean;
  neededItems: ChatCardItem[];
  readingItems: ChatCardItem[];
  /** Whether what they sent has been read, kept once it is. */
  isSentRead: boolean;
  /** Their corrections of what we heard, by the card corrected. */
  summaryEdits?: Record<string, string[]>;
  /** The card reading back what we heard that can still be corrected. */
  heardCardID?: string;
  /** The answer picked on each menu, by its message. */
  menuPicks?: Record<string, string>;
  /** The menu still being asked, whose rows answer. */
  openMenuID?: string;
}

const shownCard = (m: ChatMessage, content: CardContent, shown: Shown) => {
  switch (content.tone) {
    case 'prompts':
      return content.items
        ? {
            ...m,
            content: {
              ...content,
              items: content.items.map((item) => ({
                ...item,
                state: shown.promptStates[item.label] ?? item.state,
              })),
              isPinnable: shown.isPromptsPinnable,
            },
          }
        : m;
    case 'need':
      return { ...m, content: { ...content, items: shown.neededItems } };
    case 'reading':
      return {
        ...m,
        content: {
          ...content,
          // What they sent stays ticked once read, through any later reading.
          items: shown.readingItems.map((item, i) =>
            i === 0 && shown.isSentRead
              ? { ...item, state: 'done' as const }
              : item,
          ),
        },
      };
    case 'heard':
      return {
        ...m,
        content: {
          ...content,
          lines: shown.summaryEdits?.[m.id] ?? content.lines,
          isEditable: !!content.isEditable && m.id === shown.heardCardID,
        },
      };
    default:
      return m;
  }
};

/** A menu as drawn: its answer picked, or folded once it was answered some other way. */
const shownMenu = (m: ChatMessage, shown: Shown): ChatMessage => {
  if (m.content.kind !== 'menu') return m;
  const pickedId = shown.menuPicks?.[m.id];
  return {
    ...m,
    content: {
      ...m.content,
      pickedId,
      isClosed: !pickedId && m.id !== shown.openMenuID,
    },
  };
};

/** The menu still being asked: the last, unpicked, with nothing said to it since. */
export const openMenuID = (
  messages: readonly ChatMessage[],
  menuPicks: Record<string, string>,
): string | undefined => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.from === 'user') return undefined;
    if (m.content.kind === 'menu') return menuPicks[m.id] ? undefined : m.id;
  }
  return undefined;
};

/** The messages as drawn: what was sent shows as being read until it is, the checklists tick as facts land, and corrections show in place. */
export const shownMessages = (
  messages: readonly ChatMessage[],
  shown: Shown,
): ChatMessage[] =>
  messages
    .filter((m) => !shown.hiddenIDs.includes(m.id))
    .map((m) => {
      if (shown.sentForReading.includes(m.id) && !shown.isRead(m.id)) {
        return { ...m, isBeingRead: true };
      }
      if (m.content.kind === 'menu') return shownMenu(m, shown);
      return m.content.kind === 'card' ? shownCard(m, m.content, shown) : m;
    });
